import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { CryptoAlertTile } from "@/components/CryptoAlertTile";
import { TabSelector } from "@/components/TabSelector";
import {
  useInfiniteListings,
  useInfiniteDelistings,
} from "@/hooks/useCryptoAlerts";
import {
  CryptoAlertData,
  PositionData,
  ClosedPositionData,
} from "@crypto-trading/common/browser";
import { Loader2, Search } from "lucide-react";
import {
  AlertsFilters,
  EMPTY_ALERT_FILTERS,
  filterAlerts,
  type AlertFilterState,
} from "@/components/alerts/AlertsFilters";
import { useSavedAlerts } from "@/hooks/useSavedAlerts";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { PositionDetailDrawer } from "@/components/PositionDetailDrawer";
import { AlertDetailDrawer } from "@/components/AlertDetailDrawer";
import { PositionTradeDrawer } from "@/components/PositionTradeDrawer";
import { AlertPositionsDrawer } from "@/components/AlertPositionsDrawer";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { PageHeader } from "@/components/ui/PageHeader";
import { AlertsStatsRow } from "@/components/AlertsStatsRow";
import { AiInsightCard } from "@/components/AiInsightCard";
import { AssetClassToggle } from "@/components/AssetClassToggle";
import { trackEvent } from "@/utils/analytics";

// Same page size as desktop so both read the same shared feed pages on the api.
const ALERTS_PER_PAGE = 10;

/**
 * Mobile version of the Alerts page - maintains original mobile-optimized design
 */
export const AlertsPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [activeTab, setActiveTab] = useState<"listings" | "delistings">(
    "listings"
  );
  const [selectedPosition, setSelectedPosition] = useState<
    PositionData | ClosedPositionData | null
  >(null);
  const [selectedAlert, setSelectedAlert] = useState<CryptoAlertData | null>(
    null
  );
  const [positionsAlert, setPositionsAlert] = useState<CryptoAlertData | null>(
    null
  );
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  // React Query hooks
  const {
    data: listingsData,
    isLoading: isLoadingListings,
    fetchNextPage: fetchNextListings,
    hasNextPage: hasNextListings,
    isFetchingNextPage: isFetchingNextListings,
  } = useInfiniteListings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "listings",
    undefined
  );

  const {
    data: delistingsData,
    isLoading: isLoadingDelistings,
    fetchNextPage: fetchNextDelistings,
    hasNextPage: hasNextDelistings,
    isFetchingNextPage: isFetchingNextDelistings,
  } = useInfiniteDelistings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "delistings",
    undefined
  );

  // Flatten alerts from all pages
  const alerts =
    activeTab === "listings"
      ? (listingsData?.pages.flatMap((page) => page.alerts) ?? [])
      : (delistingsData?.pages.flatMap((page) => page.alerts) ?? []);

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<AlertFilterState>(EMPTY_ALERT_FILTERS);
  const { saved } = useSavedAlerts();
  const visibleAlerts = filterAlerts(alerts, query, filters, saved);

  const isLoading =
    activeTab === "listings" ? isLoadingListings : isLoadingDelistings;
  const hasMore =
    activeTab === "listings"
      ? (hasNextListings ?? false)
      : (hasNextDelistings ?? false);
  const isFetchingNextPage =
    activeTab === "listings"
      ? isFetchingNextListings
      : isFetchingNextDelistings;
  const fetchNextPage =
    activeTab === "listings" ? fetchNextListings : fetchNextDelistings;

  const handleOpenTradeDrawer = (alert: CryptoAlertData) => {
    setTradeSymbol(alert.currency ? ensureUsdtSymbol(alert.currency) : null);
    setIsTradeDrawerOpen(true);
  };

  const handleOpenAnalyzePage = () => {
    trackEvent("custom_trade_attempt", { page: "alerts", userId });
    navigate("/analyze");
  };

  useEffect(() => {
    if (selectedAlert) {
      trackEvent("alert_drawer_view", {
        userId,
        alertId: selectedAlert.id,
        symbol: selectedAlert.currency,
        alertType: selectedAlert.type,
        tier: selectedAlert.tier ?? null,
        exchange: selectedAlert.exchange,
      });
    }
  }, [selectedAlert?.id]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || isLoading || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, isFetchingNextPage, activeTab, fetchNextPage]);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto flex flex-col gap-6 pt-2">
          <PageHeader
            title="Market"
            accent="Alerts"
            subtitle="Real-time listing and delisting alerts across global exchanges."
          />

          <AlertsStatsRow onUpcomingClick={handleOpenAnalyzePage} />

          <div className="flex gap-2">
            <AssetClassToggle
              value="crypto"
              onChange={(value) => {
                if (value === "stocks") navigate("/alerts/stocks");
              }}
              className="flex-1"
            />
            <TabSelector
              options={[
                { value: "listings", label: "Listing" },
                { value: "delistings", label: "Delisting" },
              ]}
              value={activeTab}
              onChange={setActiveTab}
              className="flex-1"
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <label className="glass flex h-10 min-w-0 flex-1 items-center gap-2 px-3">
              <Search className="size-4 shrink-0 text-[#a3a3a3]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search token or exchange"
                className="min-w-0 flex-1 bg-transparent text-base text-white outline-none placeholder:text-[#737373]"
              />
            </label>
            <AlertsFilters alerts={alerts} value={filters} onChange={setFilters} variant="icon" />
          </div>

          <AiInsightCard onClick={handleOpenAnalyzePage} />

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          )}

          {/* Alerts List */}
          {!isLoading && visibleAlerts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-text-secondary">
                {alerts.length === 0 ? `No ${activeTab} found` : "Nothing matches your search or filters"}
              </p>
            </div>
          )}

          {!isLoading && (
            <div className="space-y-4">
              {visibleAlerts.map((alert) => (
                <CryptoAlertTile
                  key={alert.id}
                  alert={alert}
                  onViewPositions={setPositionsAlert}
                  onDetailsClick={() => setSelectedAlert(alert)}
                  onTrade={handleOpenTradeDrawer}
                />
              ))}
            </div>
          )}

          {/* Load More Trigger */}
          {!isLoading && hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              {isFetchingNextPage && (
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
              )}
            </div>
          )}

          {/* End of List */}
          {!isLoading && !hasMore && alerts.length > 0 && (
            <div className="text-center py-8">
              <p className="text-text-secondary text-sm">
                No more {activeTab} to load
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Alert Detail Drawer */}
      <AlertDetailDrawer
        userId={userId}
        alert={selectedAlert}
        open={!!selectedAlert}
        onOpenChange={(open) => !open && setSelectedAlert(null)}
        onTrade={(alert) => {
          setTradeSymbol(
            alert.currency ? ensureUsdtSymbol(alert.currency) : null
          );
          setIsTradeDrawerOpen(true);
        }}
      />

      <AlertPositionsDrawer
        alert={positionsAlert}
        open={!!positionsAlert}
        onOpenChange={(open) => !open && setPositionsAlert(null)}
        onSelectPosition={(position) => {
          setPositionsAlert(null);
          setSelectedPosition(position);
        }}
        onTrade={(alert) => {
          setPositionsAlert(null);
          handleOpenTradeDrawer(alert);
        }}
      />

      {/* Position Detail Drawer */}
      <PositionDetailDrawer
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        hideCloseAction={true}
        source="alerts_page"
      />

      <PositionTradeDrawer
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => {
          setIsTradeDrawerOpen(open);
          if (!open) {
            setTradeSymbol(null);
          }
        }}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
          }
        }}
      />

      <ReferralBottomNav />
    </div>
  );
};
